import styled, { css } from 'styled-components'

import { colors } from '@/styles'
import { Label } from '@/components/text'

export type StatusBadgeType =
  | 'error'
  | 'success'
  | 'default'
  | 'warning'
  | 'info'
  | 'blue'
  | 'pending'
  | 'link'

type StatusBadgeProps = {
  type: StatusBadgeType
  size?: 'small' | 'medium'
  space?: 'fit' | 'full'
  title: React.ReactNode
}

type StyleProps = Omit<StatusBadgeProps, 'title' | 'tooltip'>

const blue = css`
  background-color: ${colors.blue96};
  color: ${colors.blue16};
  svg {
    path {
      fill: ${colors.blue16};
    }
  }
`

const error = css`
  background-color: ${colors.red96};
  color: ${colors.red64};
  svg {
    path {
      fill: ${colors.red64};
    }
  }
`

const success = css`
  background-color: #ebfffb;
  color: #00a38b;
  svg {
    path {
      fill: #00a38b;
    }
  }
`

const info = css`
  background-color: ${colors.purple96};
  color: ${colors.purple40};
  svg {
    path {
      fill: ${colors.purple40};
    }
  }
`

const warning = css`
  background-color: #fffbd8;
  color: #8c7317;
  svg {
    path {
      fill: #8c7317;
    }
  }
`

const pending = css`
  background-color: ${colors.yellow96};
  color: ${colors.yellow24};
  svg {
    path {
      fill: ${colors.yellow24};
    }
  }
`

const full = css`
  width: 100%;
  div {
    width: 100%;
    text-align: center;
  }
`

const link = css`
  color: ${colors.link};
  background-color: ${colors.white};

  div {
    width: 100%;
    text-align: left;
  }
`

const getTypeStyles = (props: StyleProps) => {
  if (props.type === 'error') return error
  if (props.type === 'success') return success
  if (props.type === 'info') return info
  if (props.type === 'warning') return warning
  if (props.type === 'blue') return blue
  if (props.type === 'pending') return pending
  if (props.type === 'link') return link
  return
}

const getSizeStyles = (size: StyleProps['size']) => {
  if (size === 'small') return '12px'
  return '14px'
}

const getSpaceStyles = (space: StyleProps['space']) => {
  if (space === 'full') return full
}

const Wrapper = styled.div<StyleProps>`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 16px;
  width: fit-content;
  height: 24px;
  gap: 0 8px;
  background-color: ${colors.grey96};
  color: ${colors.grey56};
  svg {
    margin-bottom: 2px;
    path {
      fill: ${colors.grey56};
    }
  }
  ${props => getTypeStyles(props)};
  ${props => getSpaceStyles(props.space)};
`

const StatusBadge: React.FC<StatusBadgeProps> = ({
  type = 'default',
  size = 'medium',
  space = 'fit',
  title
}) => {
  return (
    <Wrapper type={type} space={space}>
      <Label fontSize={getSizeStyles(size)}>{title}</Label>
    </Wrapper>
  )
}

export default StatusBadge
